import { StrictMode, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { AxiosError } from "axios";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { Amplify } from "aws-amplify";
import { useAtom } from "jotai";
import { appearanceAtom } from "@/stores/appearance-atom";
import { LoadingScreen } from "./components/ui/loading-screen";
import { Toaster } from "./components/ui/sonner";
import "./index.css";
import { routeTree } from "./routeTree.gen";
import * as Sentry from "@sentry/react";

if (import.meta.env.VITE_ENV === "prod") {
  Sentry.init({
    dsn: "https://0736d5b631bff920a121da4234521772@o4506727461879808.ingest.us.sentry.io/4509112246403072",
    environment: import.meta.env.VITE_ENV,
  });
}

function GlobalAppearanceEffect() {
  const [appearance] = useAtom(appearanceAtom);

  useEffect(() => {
    const root = window.document.documentElement;

    // Apply theme (light/dark)
    const applyTheme = (theme: "dark" | "light") => {
      root.classList.remove("light", "dark");
      root.classList.add(theme);
    };
    applyTheme(appearance.theme);

    // Apply font
    root.classList.remove("font-inter", "font-poppins", "font-geist-mono");
    root.classList.add(`font-${appearance.font}`);

    // Apply color palette
    root.classList.remove("default", "nature", "retro");
    root.classList.add(appearance.color);

    return () => {
      // No cleanup needed for theme listeners
    };
  }, [appearance]);

  return null;
}

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
    },
  },
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (import.meta.env.DEV) {
          console.log({ failureCount, error });
        }
        if (failureCount >= 1 && import.meta.env.DEV) return false;
        if (failureCount > 3 && import.meta.env.PROD) return false;

        return !(
          error instanceof AxiosError &&
          [401, 403].includes(error.response?.status ?? 0)
        );
      },
      refetchOnWindowFocus: false,
      staleTime: 10 * 1000, // 10s
    },
    mutations: {
      onError: (error) => {
        console.error(error);
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof AxiosError) {
        console.error(error);
      }
    },
  }),
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPendingMinMs: 0,
  defaultPendingMs: 0,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: () => (
    <LoadingScreen text="Checking authentication..." />
  ),
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, {
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Uncaught error", error, errorInfo.componentStack);
    }),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  });
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <GlobalAppearanceEffect />
        <RouterProvider router={router} />
        <Toaster
          position="top-right"
          expand
          richColors={true}
          closeButton={true}
        />
      </QueryClientProvider>
    </StrictMode>,
  );
}
